import { useState } from 'react';

import type { PostInsightsData } from '@shared/dream-components';

import MasqueradeBanner from '~/components/Actions/MasqueradeBanner';
import StagingPreview from '~/components/Actions/StagingPreviewAction';
import SignupFlow from '~/components/SignupFlow';
import type { CookieValues, SetCookieOptions } from '~/hooks/useUtils/useCookies';
import useSignupRedirects from '~/hooks/useUtils/useSignupRedirects';
import type { Subscription } from '~/interfaces/subscriptions';

import { PageContext, type PageModalType } from './PageContext';

interface PageProviderProps {
  children: React.ReactNode;
  cookies: CookieValues;
  setCookie: (options: SetCookieOptions) => void;
  removeCookie: (name: SetCookieOptions['name']) => void;
  cookiesSet?: boolean;
  visitToken?: string;
  referralId?: string;
  recaptchaSiteKey?: string;
  currentPath: string;
  subscription?: Subscription;
  isStagingPreview?: boolean;
  isMasquerading?: boolean;
  masqueradeEmail?: string;
}

export const PageProvider: React.FC<PageProviderProps> = ({
  cookies,
  setCookie,
  removeCookie,
  cookiesSet,
  children,
  visitToken,
  referralId,
  recaptchaSiteKey,
  currentPath,
  subscription,
  isStagingPreview,
  isMasquerading,
  masqueradeEmail,
}) => {
  const { isInProgress } = useSignupRedirects();
  const [openPageModalType, setOpenPageModalType] = useState<PageModalType>(null);
  const [closeModalActionDispatched, setCloseModalActionDispatched] = useState(false);
  const [postsInsights, setPostsInsights] = useState<Record<string, PostInsightsData>>({});

  return (
    <PageContext.Provider
      value={{
        cookies,
        setCookie,
        removeCookie,
        cookiesSet,
        visitToken,
        referralId,
        currentPath,
        recaptchaSiteKey,
        subscription,
        openPageModalType,
        setOpenPageModalType,
        closeModalActionDispatched,
        setCloseModalActionDispatched,
        postsInsights,
        setPostsInsights,
      }}
    >
      {isInProgress && <SignupFlow>{children}</SignupFlow>}
      {isStagingPreview && <StagingPreview />}
      {isMasquerading && <MasqueradeBanner email={masqueradeEmail} />}
      {!isInProgress && children}
    </PageContext.Provider>
  );
};
